import { useTranslation } from 'react-i18next';
import { CommonDocumentService } from '@umanforge/common';

import { StorageStatValue } from './storage_stat_value.component';


interface StorageSummaryProps {
  title: string;
  /** Quota total du client en Ko (kilo-octets) */
  quota: number;
  /** Espace réellement utilisé en Ko (optionnel). */
  totalUsed?: number;
  /** Espace disponible en Ko provenant du cloud (optionnel). Si présent, remplace le calcul `quota - used`. */
  available?: number;
  /** Seuil % à partir duquel la barre/valeur passent en orange */
  warningPercent?: number;
  /** Seuil % à partir duquel la barre/valeur passent en rouge */
  dangerPercent?: number;
}

export function StorageSummary( {
  title,
  quota,
  totalUsed,
  available: availableProp,
  warningPercent = 80,
  dangerPercent = 100,
}: StorageSummaryProps ) {
  const { t } = useTranslation();

  const used = totalUsed ?? 0;
  const available = availableProp !== undefined ? availableProp : Math.max( quota - used, 0 );
  const percent = quota > 0 ? Math.min( Math.round( ( used / quota ) * 100 ), 100 ) : 0;

  const accentColor =
    dangerPercent !== undefined && percent >= dangerPercent ? 'text-red-500' :
      warningPercent !== undefined && percent >= warningPercent ? 'text-orange-400' :
        'text-green-600';

  const barColor =
    dangerPercent !== undefined && percent >= dangerPercent ? 'bg-red-500' :
      warningPercent !== undefined && percent >= warningPercent ? 'bg-orange-400' :
        'bg-green-600';

  return (
    <div className='flex flex-col gap-3 p-4 rounded-lg border border-gray-200 bg-white shadow-sm w-full'>
      <h2 className='text-base font-semibold text-gray-800'>{ title }</h2>

      <div className='flex items-start gap-6'>

        <StorageStatValue
          label={ t( 'translation:client_drive_quota' ) }
          value={ quota }
          className='min-w-[120px]'
        />

        <div className='flex flex-col gap-1 flex-1'>
          <span className='text-sm font-semibold text-gray-800'>{ t( 'translation:total_used_storage' ) }</span>
          <span className={ `text-lg font-semibold ${ accentColor }` }>
            { CommonDocumentService.getReadableFileSizeString( used ) } ({ percent } %)
          </span>
          <div className='w-full h-2 rounded-full bg-gray-200 overflow-hidden mt-0.5'>
            <div
              className={ `h-full rounded-full transition-all duration-300 ${ barColor }` }
              style={ { width: `${ percent }%` } }
            />
          </div>
        </div>

        <StorageStatValue
          label={ t( 'translation:available_storage' ) }
          value={ available }
          className='min-w-[120px] text-right items-end'
        />

      </div>
    </div>
  );
}
